import { ISubcategoryPageCourseHit } from '@/types/search'
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch'
import { useIntersectionObserver, useIsomorphicLayoutEffect } from 'usehooks-ts'
import { NewCourseCard } from '../../course/components/NewCourseCard'
import { AvailableLanguages } from '../../shared/providers/LanguageSelectionProvider'
import { adaptCourseCollectionToCourseCard } from '../lib/course-adapter'
import { GroupItemsSection } from './GroupItemsSection'
import { SearchErrorState } from './SearchErrorState'
import { SearchLoadingState } from './SearchLoadingState'

type SubCategoryPageCSRResultsProps<L extends AvailableLanguages> = {
  locale: L
}

export function SubCategoryPageCSRResults<L extends AvailableLanguages>({
  locale,
}: SubCategoryPageCSRResultsProps<L>) {
  const {
    items = [],
    isLastPage,
    showMore,
  } = useInfiniteHits<ISubcategoryPageCourseHit<L>>()
  const { status, refresh } = useInstantSearch()

  const isStalled = status === 'stalled'
  const isLoading = status === 'loading'
  const isError = status === 'error'

  const { isIntersecting: isVisible, ref } = useIntersectionObserver({
    initialIsIntersecting: false,
  })

  useIsomorphicLayoutEffect(() => {
    if (status !== 'idle' || isLastPage || !isVisible) {
      return
    }

    showMore()
  }, [isVisible, isLastPage, status, showMore])

  const handleErrorRetry = () => {
    refresh()
  }

  return (
    <>
      {isError ? (
        <SearchErrorState
          onRetry={handleErrorRetry}
          className="md:min-h-[65vh]"
        />
      ) : (isLoading || isStalled) && !items.length ? (
        <SearchLoadingState className="flex flex-col gap-12" />
      ) : (
        <>
          <GroupItemsSection>
            {items.map((course) => (
              <NewCourseCard
                key={course.id}
                course={adaptCourseCollectionToCourseCard<L>(course, locale)}
                className="h-full"
              />
            ))}
          </GroupItemsSection>
          {(isLoading || isStalled) && (
            <SearchLoadingState className="flex flex-col gap-12" bare />
          )}
          {items.length > 0 && !isLastPage && (
            <div ref={ref} aria-hidden="true" />
          )}
        </>
      )}
    </>
  )
}
